import { useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import useApi from '../../../hooks/useApi';
import Table from '../../../components/Table';
import apiService from '../../../services/apiService';
import { FaEdit, FaEye, FaTrash } from 'react-icons/fa'; // Removed FaDownload as it's not used
import { Country } from 'country-state-city';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function Users() {
  const { data: users, loading: usersLoading, fetchData: fetchUsers } = useApi('/users');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate(); // Initialize useNavigate

  const handleAdd = () => {
    navigate('/users/new'); // Redirect to add mode
  };

  const handleEdit = (user) => {
    navigate(`/users/${user._id}`); // Redirect to edit mode with user ID
  };

  const handleView = (user) => {
    navigate(`/users/${user._id}`, { state: { viewMode: true } }); // Redirect to view mode with state
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      setIsLoading(true);
      await apiService.delete(`/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to delete user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDocument = async (userId, doc) => {
    try {
      const { blob, contentType } = await apiService.getBlob(`/users/document/${userId}/${doc._id}`);
      if (!contentType.includes('application/pdf') && !contentType.match(/image\/(jpeg|png)/)) {
        throw new Error('Unsupported file format. Please download to view.');
      }
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank'); // Open in new tab
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000); // Cleanup after 10 seconds
    } catch (err) {
      console.error('handleViewDocument error:', err);
      toast.error(err.message || 'Failed to load document');
    }
  };

  const handleViewProfilePicture = async (userId, profilePicture) => {
    try {
      const { blob, contentType } = await apiService.getBlob(`/users/document/${userId}/profilePicture`);
      if (!contentType.match(/image\/(jpeg|png)/)) {
        throw new Error('Unsupported profile picture format.');
      }
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank'); // Open in new tab
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000); // Cleanup after 10 seconds
    } catch (err) {
      console.error('handleViewProfilePicture error:', err);
      toast.error(err.message || 'Failed to load profile picture');
    }
  };

  const handleDeleteDocument = async (userId, documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await apiService.delete(`/users/document/${userId}/${documentId}`);
      toast.success('Document deleted successfully');
      fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to delete document');
    }
  };

  const columns = [
    {
      key: 'fullName',
      label: 'Name',
      render: (user) => `${user.firstName} ${user.lastName}`,
    },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    {
      key: 'phone',
      label: 'Phone',
      render: (user) => user.phone || 'N/A',
    },
    {
      key: 'country',
      label: 'Country',
      render: (user) => user.country ? Country.getCountryByCode(user.country)?.name || 'N/A' : 'N/A',
    },
    {
      key: 'availability',
      label: 'Availability',
      render: (user) =>
        user.role === 'specialist'
          ? user.availability === 'available'
            ? 'Available'
            : 'Not Available'
          : 'N/A',
    },
    {
      key: 'profilePicture',
      label: 'Profile',
      render: (user) =>
        user.profilePicture ? (
          <button
            onClick={() => handleViewProfilePicture(user._id, user.profilePicture)}
            className="text-blue-600 hover:text-blue-700"
            title="View Profile Picture"
          >
            <img
              src={`${BASE_URL}${user.profilePicture}`}
              alt="Profile"
              className="w-8 h-8 rounded-full object-cover"
            />
          </button>
        ) : (
          'None'
        ),
    },
    {
      key: 'documents',
      label: 'Documents',
      render: (user) => (
        <div className="flex space-x-2">
          {user.verificationDocuments.length > 0 ? (
            user.verificationDocuments.map((doc) => (
              <div key={doc._id} className="flex items-center space-x-2">
                <button
                  onClick={() => handleViewDocument(user._id, doc)}
                  className="text-blue-600 hover:text-blue-700"
                  title={`View ${doc.type}`}
                >
                  <FaEye />
                </button>
                <button
                  onClick={() => handleDeleteDocument(user._id, doc._id)}
                  className="text-red-600 hover:text-red-700"
                  title="Delete Document"
                >
                  <FaTrash />
                </button>
              </div>
            ))
          ) : (
            'None'
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (user) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleView(user)}
            className="text-green-600 hover:text-green-700"
            title="View"
          >
            <FaEye />
          </button>
          <button
            onClick={() => handleEdit(user)}
            className="text-yellow-600 hover:text-yellow-700"
            title="Edit"
          >
            <FaEdit />
          </button>
          <button
            onClick={() => handleDelete(user._id)}
            className="text-red-600 hover:text-red-700"
            title="Delete"
            disabled={isLoading}
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto p-6 bg-white rounded-lg shadow-xl">
      <div className="flex justify-between items-center mb-8 border-b border-gray-200 pb-4">
        <h2 className="text-3xl font-bold text-gray-900">User Management</h2>
        <button
          onClick={handleAdd}
          className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition duration-200"
        >
          Add User
        </button>
      </div>
      {usersLoading ? (
        <div className="text-center text-gray-600">Loading...</div>
      ) : (
        <div className="overflow-x-auto">
          <Table columns={columns} data={users || []} className="min-w-full" />
        </div>
      )}
    </div>
  );
}

export default Users;